// src/modules/app-views/omnichannel/pages/Init/components/ReactionPicker.jsx
// *******************************************************************************
// REACTION PICKER — WhatsApp-style quick emoji reaction popover
// *******************************************************************************

import { useState, useCallback } from 'react';
import { Popover, Tooltip } from 'antd';
import { SmileOutlined } from '@ant-design/icons';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************

// WhatsApp default quick reactions
const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

// *******************************************************************************
// COMPONENT
// *******************************************************************************

const ReactionPicker = ({ msgIndex, currentReactionEmoji, onReact, isDark, direction = 'inbound' }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [open, setOpen] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleEmojiClick = useCallback(
    emoji => {
      setOpen(false);
      // If clicking the same emoji that's already reacted, remove it
      if (emoji === currentReactionEmoji) {
        onReact(msgIndex, null);
      } else {
        onReact(msgIndex, emoji);
      }
    },
    [msgIndex, currentReactionEmoji, onReact]
  );

  const handleOpenChange = useCallback(visible => {
    setOpen(visible);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  const pickerContent = (
    // Outer wrapper with paddingBottom bridges the gap between popover and trigger
    <div style={{ paddingBottom: 8 }}>
      <div
        style={{
          display: 'flex',
          gap: 2,
          padding: '4px 6px',
          borderRadius: 24,
          background: isDark ? '#1f2c34' : '#ffffff',
          boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
        }}
      >
        {QUICK_REACTIONS.map(emoji => (
          <span
            key={emoji}
            onClick={e => {
              e.stopPropagation();
              handleEmojiClick(emoji);
            }}
            style={{
              fontSize: 22,
              cursor: 'pointer',
              padding: '2px 4px',
              borderRadius: 8,
              transition: 'transform 0.15s, background 0.15s',
              background:
                emoji === currentReactionEmoji
                  ? isDark
                    ? 'rgba(255,255,255,0.15)'
                    : 'rgba(0,0,0,0.08)'
                  : 'transparent',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.transform = 'scale(1.3)';
              e.currentTarget.style.background = isDark
                ? 'rgba(255,255,255,0.1)'
                : 'rgba(0,0,0,0.05)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.background =
                emoji === currentReactionEmoji
                  ? isDark
                    ? 'rgba(255,255,255,0.15)'
                    : 'rgba(0,0,0,0.08)'
                  : 'transparent';
            }}
          >
            {emoji}
          </span>
        ))}
      </div>
    </div>
  );

  return (
    <Popover
      content={pickerContent}
      trigger="click"
      open={open}
      onOpenChange={handleOpenChange}
      placement="top"
      arrow={false}
      align={{ offset: [0, 8] }}
      styles={{
        body: {
          padding: 0,
          background: 'transparent',
          boxShadow: 'none',
        },
        root: {
          // Remove the outer card (ant-popover-inner) background and shadow
        },
        container: {
          padding: 0,
          background: 'transparent',
          boxShadow: 'none',
        },
      }}
    >
      <Tooltip title="Reagir">
        <span
          onClick={e => e.stopPropagation()}
          style={{
            cursor: 'pointer',
            fontSize: 16,
            opacity: 0.5,
            transition: 'opacity 0.15s',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 28,
            ...(direction === 'outbound' ? { marginLeft: -30 } : { marginRight: -44 }),
            borderRadius: '50%',
            background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.opacity = '1';
            e.currentTarget.style.background = isDark
              ? 'rgba(255,255,255,0.15)'
              : 'rgba(0,0,0,0.08)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.opacity = '0.5';
            e.currentTarget.style.background = isDark
              ? 'rgba(255,255,255,0.08)'
              : 'rgba(0,0,0,0.04)';
          }}
        >
          <SmileOutlined />
        </span>
      </Tooltip>
    </Popover>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************

export default ReactionPicker;
